/**
 * นำเข้าข้อมูลย้อนหลัง (OWNER) — เงินเดือน/ภาษี/ปกส. รายเดือน + ประวัติการลา
 * ให้ข้อมูลครบทั้งปี (สำหรับภาษีสะสม + รายงานปลายปี ภงด.1/50ทวิ/ปกส.).
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAuthStore } from '../../lib/store';
import { useToast } from '../../components/ui/toast';
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787';

interface ImportResult {
  written: number;
  skipped: number;
  errors: Array<{ row: number; msg: string }>;
  years_recomputed?: number[];
}

function downloadCsv(filename: string, header: string[], example: string[]) {
  const csv = '﻿' + header.join(',') + '\n' + example.join(',') + '\n';
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function ImportCard({
  title, description, endpoint, templateName, columns, example, note,
}: {
  title: string;
  description: string;
  endpoint: string;
  templateName: string;
  columns: { key: string; label: string; required?: boolean }[];
  example: string[];
  note?: string;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const jwt = useAuthStore.getState().jwt;
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
        body: form,
      });
      const json = (await res.json()) as { data?: ImportResult; error?: string; message?: string };
      if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
      setResult(json.data ?? null);
      if (json.data) toast.success(`นำเข้าแล้ว ${json.data.written} รายการ`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'นำเข้าไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-start gap-2">
        <FileSpreadsheet className="w-5 h-5 text-teal-600 mt-0.5" />
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr><th className="px-2 py-1 text-left">คอลัมน์</th><th className="px-2 py-1 text-left">ความหมาย</th></tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c.key} className="border-t border-gray-100">
                <td className="px-2 py-1 font-mono">{c.key}{c.required && <span className="text-red-500">*</span>}</td>
                <td className="px-2 py-1 text-gray-600">{c.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {note && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{note}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => downloadCsv(templateName, columns.map((c) => c.key), example)} className="btn-secondary">
          <Download className="w-4 h-4" /> ดาวน์โหลดเทมเพลต
        </button>
        <label className="btn-primary cursor-pointer">
          <Upload className="w-4 h-4" /> {busy ? 'กำลังนำเข้า…' : 'อัปโหลด CSV'}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} disabled={busy} />
        </label>
      </div>

      {result && (
        <div className="rounded-lg bg-gray-50 p-3 text-sm space-y-1">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-4 h-4" /> นำเข้าสำเร็จ {result.written} รายการ
            {result.skipped > 0 && <span className="text-gray-500">· ข้าม {result.skipped}</span>}
          </div>
          {result.years_recomputed && result.years_recomputed.length > 0 && (
            <div className="text-xs text-gray-500">อัปเดตยอดสะสมปี: {result.years_recomputed.join(', ')}</div>
          )}
          {result.errors.length > 0 && (
            <div className="text-xs text-red-600">
              <div className="font-medium">ผิดพลาด {result.errors.length} แถว:</div>
              <ul className="list-disc ml-4 max-h-32 overflow-y-auto">
                {result.errors.slice(0, 50).map((er, i) => <li key={i}>แถว {er.row}: {er.msg}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function HistoricalImport() {
  return (
    <AdminLayout title="นำเข้าข้อมูลย้อนหลัง" subtitle="เติมข้อมูลเดือนก่อนเริ่มใช้ระบบ ให้ครบทั้งปี (สำหรับภาษีสะสม + รายงานปลายปี)">
      <div className="space-y-5">
        <p className="card p-3 text-sm text-gray-600 bg-blue-50 border-blue-100">
          เหมาะกับบริษัทที่ย้ายมา PayPilot กลางปี — นำเข้ายอดเดือนที่ผ่านมา เพื่อให้ ภงด.1 / 50 ทวิ / ปกส.
          และการคิดภาษีแบบสะสมของงวดถัดไปถูกต้องครบทั้งปี
        </p>

        <ImportCard
          title="เงินเดือน / ภาษี / ปกส. รายเดือน"
          description="ยอดเดือนย้อนหลังต่อพนักงาน — เขียนเป็นงวดที่ปิดแล้ว และอัปเดตยอดสะสมทั้งปีให้อัตโนมัติ"
          endpoint="/owner/import/payroll-history"
          templateName="payroll-history-template.csv"
          columns={[
            { key: 'period', label: 'งวด YYYY-MM เช่น 2026-01', required: true },
            { key: 'emp_code', label: 'รหัสพนักงาน', required: true },
            { key: 'gross', label: 'รายได้รวม (บาท)', required: true },
            { key: 'taxable', label: 'เงินได้ที่ต้องเสียภาษี (เว้นว่าง = ใช้รายได้รวม)' },
            { key: 'tax', label: 'ภาษีหัก ณ ที่จ่าย' },
            { key: 'sso', label: 'ประกันสังคม' },
            { key: 'pf', label: 'กองทุนสำรองเลี้ยงชีพ' },
            { key: 'net', label: 'เงินสุทธิ (เว้นว่าง = คำนวณให้)' },
          ]}
          example={['2026-01', 'EMP001', '30000', '30000', '500', '750', '0', '28750']}
          note="งวดที่ระบบคิดเงินเดือนจริงไปแล้วจะไม่ถูกทับ (ข้ามให้อัตโนมัติ) — นำเข้าได้เฉพาะงวดที่ยังไม่มีข้อมูล"
        />

        <ImportCard
          title="ประวัติการลางาน"
          description="วันลาที่อนุมัติแล้วในอดีต — บันทึกเป็นสถานะอนุมัติ (วันละ 1 แถว)"
          endpoint="/owner/import/leave-history"
          templateName="leave-history-template.csv"
          columns={[
            { key: 'emp_code', label: 'รหัสพนักงาน', required: true },
            { key: 'date', label: 'วันที่ลา YYYY-MM-DD', required: true },
            { key: 'leave_type', label: 'ชนิดลา: sick/personal/vacation/unpaid/maternity', required: true },
            { key: 'period', label: 'ช่วง: full/morning/afternoon (เว้นว่าง = full)' },
            { key: 'reason', label: 'เหตุผล (ไม่บังคับ)' },
          ]}
          example={['EMP001', '2026-02-14', 'sick', 'full', 'ลาป่วย']}
          note="ถ้ามีวันลาของพนักงานคนนั้นในวันเดียวกันอยู่แล้ว จะข้ามให้ (กันซ้ำ)"
        />
      </div>
    </AdminLayout>
  );
}
